import { useMemo } from "react";

import { type ExclusiveLensStatus, type Visibility } from "../../gql-generated/types";
import { type LensVisibilityConfig, makeLensSubmissionVisibilityConfig } from "../../lens-visibility/hooks";
import { useLensFolderPermissions } from "../../shared/hooks/roles";
import { type ApolloResult } from "../../shared/models";

interface WebLensVisibilityConfigInput {
    lensFolderId: string;
    lensForUpdate?: {
        visibility: Visibility;
        exclusiveLensStatus: ExclusiveLensStatus;
    } | null;
}

/** Uses the Lens's actual folder rather than route selection, so deep-linked updates honor roles. */
export const useWebLensVisibilityConfig = ({
    lensFolderId,
    lensForUpdate,
}: WebLensVisibilityConfigInput): ApolloResult<{ visibilityConfig: LensVisibilityConfig }> => {
    const { lensFolderPermissions, loading, error } = useLensFolderPermissions(lensFolderId);
    const visibility = lensForUpdate?.visibility;
    const exclusiveLensStatus = lensForUpdate?.exclusiveLensStatus;

    const visibilityConfig = useMemo(
        () =>
            makeLensSubmissionVisibilityConfig(
                lensFolderPermissions,
                visibility !== undefined && exclusiveLensStatus !== undefined
                    ? { visibility, exclusiveLensStatus }
                    : undefined,
            ),
        [exclusiveLensStatus, lensFolderPermissions, visibility],
    );

    return { visibilityConfig, loading, error };
};
